import { Button, Group, Modal, Stack, Text, TextInput } from "@mantine/core";
import type { IConnectedBimSpace } from "@schemas/issueSchema";
import React, { useState } from "react";
import { usePostProjectByProjectIdIssuesCreateMutation } from "../../../../../../openapi";
import { useAppDispatch } from "../../../../../hooks";
import { thunkOpenIssueModalById } from "../../issueSlice";

const getConnectedBimSpacesPayload = (spaces: IConnectedBimSpace[]) =>
  spaces.map((space) => ({
    ...space,
    roomNumber: space.roomNumber ?? "",
  }));

const FloorplanCreateIssueModal: React.FC<{
  opened: boolean;
  projectId: string;
  issueBoardId: string;
  connectedBimSpaces: IConnectedBimSpace[];
  onClose: () => void;
  onCreated?: () => void;
}> = ({ opened, projectId, issueBoardId, connectedBimSpaces, onClose, onCreated }) => {
  const dispatch = useAppDispatch();
  const [newIssueTitle, setNewIssueTitle] = useState("");
  const [createIssue, { isLoading: isCreatingIssue }] = usePostProjectByProjectIdIssuesCreateMutation();

  const connectedRoomCount = connectedBimSpaces.length;
  const canCreateIssue = connectedRoomCount > 0 && newIssueTitle.trim() !== "" && !isCreatingIssue;

  const handleClose = () => {
    setNewIssueTitle("");
    onClose();
  };

  const handleCreateIssue = async () => {
    if (!canCreateIssue) {
      return;
    }

    const createdIssueResp = await createIssue({
      projectId,
      body: {
        issueBoardId,
        title: newIssueTitle.trim(),
        connectedBimSpaces: getConnectedBimSpacesPayload(connectedBimSpaces),
      },
    }).unwrap();

    setNewIssueTitle("");
    onClose();
    onCreated?.();
    dispatch(thunkOpenIssueModalById(createdIssueResp._id));
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={connectedRoomCount > 1 ? `Ny sak for ${connectedRoomCount} rom` : "Ny sak"}
      size="sm"
    >
      <Stack gap="sm">
        <TextInput
          data-autofocus
          placeholder="Skriv inn navn på sak"
          value={newIssueTitle}
          onChange={(event) => setNewIssueTitle(event.currentTarget.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && canCreateIssue) {
              event.preventDefault();
              void handleCreateIssue();
            }
          }}
        />

        {connectedRoomCount > 1 && (
          <Text size="sm" c="dimmed">
            Saken kobles til {connectedRoomCount} valgte rom.
          </Text>
        )}

        <Group justify="space-between">
          <Button variant="outline" onClick={handleClose}>
            Avbryt
          </Button>
          <Button
            color="mc-primary"
            onClick={() => void handleCreateIssue()}
            disabled={!canCreateIssue}
            loading={isCreatingIssue}
          >
            Opprett
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default FloorplanCreateIssueModal;
